/**
 * Pharmacy portal: clinics (PH-005), points of contact (S-17), users (PH-001)
 * and reply times (PH-012).
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, EmptyState, ErrorState, Field, LoadingRows, Table, Td, Th, inputClass } from '../../components/ui';
import { pharmacyApi, shortDate, type PharmacyRole } from '../../lib/pharmacy';
import { usePharmacyMe } from './PharmacyApp';

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');

function Chip({ tone = 'grey', children }: { tone?: 'green' | 'amber' | 'grey'; children: string }) {
  const cls = tone === 'green' ? 'bg-[#e3efe9] text-forest' : tone === 'amber' ? 'bg-[#fbf0d6] text-amber-deep' : 'bg-[#eef0f3] text-[#4d6072]';
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${cls}`}>{children}</span>;
}

/* ------------------------------------------------------------------ *
 * Clinics
 * ------------------------------------------------------------------ */

interface ClinicRow {
  clinicId: string;
  clinicName: string;
  status: 'requested' | 'active';
  accountRef: string | null;
  priceList: { id: string; name: string } | null;
  requestedAt: string;
  linkedAt: string | null;
}

export function PharmacyClinicsPage() {
  const qc = useQueryClient();
  const me = usePharmacyMe();
  const canAccept = me.data?.user.role === 'admin' || me.data?.user.role === 'pricing';
  const clinics = useQuery({ queryKey: ['pharmacy-clinics'], queryFn: () => pharmacyApi.get<{ clinics: ClinicRow[] }>('/clinics') });
  const lists = useQuery({ queryKey: ['pharmacy-lists'], queryFn: () => pharmacyApi.get<{ lists: Array<{ id: string; name: string }> }>('/price-lists'), enabled: canAccept });
  const [choice, setChoice] = useState<Record<string, string>>({});
  const accept = useMutation({
    mutationFn: (clinicId: string) => pharmacyApi.post(`/clinics/${clinicId}/accept`, { priceListId: choice[clinicId] }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pharmacy-clinics'] });
      void qc.invalidateQueries({ queryKey: ['pharmacy-me'] });
    },
  });
  if (clinics.error) return <ErrorState error={clinics.error} retry={() => void clinics.refetch()} />;
  if (!clinics.data) return <LoadingRows rows={4} />;
  return (
    <Card title="Clinics" subtitle="Clinics that use you. You see their name and what they send you, never their patients' prices or plans.">
      {clinics.data.clinics.length === 0 ? (
        <EmptyState title="No clinics yet" body="A clinic asks to link to you from its own settings. You then choose which of your price lists it is on." />
      ) : (
        <Table minWidth={640}>
          <thead>
            <tr><Th>Clinic</Th><Th>Status</Th><Th>Price list</Th><Th>Account</Th><Th align="right">{''}</Th></tr>
          </thead>
          <tbody>
            {clinics.data.clinics.map((c) => (
              <tr key={c.clinicId}>
                <Td className="font-semibold text-navy">{c.clinicName}</Td>
                <Td>{c.status === 'active' ? <Chip tone="green">Linked</Chip> : <Chip tone="amber">Asking to link</Chip>}</Td>
                <Td>
                  {c.status === 'active' || !canAccept ? (c.priceList?.name ?? '-') : (
                    <select className={`${inputClass} py-2`} value={choice[c.clinicId] ?? ''} onChange={(e) => setChoice({ ...choice, [c.clinicId]: e.target.value })} aria-label={`Price list for ${c.clinicName}`}>
                      <option value="">Choose a list</option>
                      {(lists.data?.lists ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  )}
                </Td>
                <Td>{c.accountRef ?? '-'}</Td>
                <Td align="right">
                  {c.status === 'requested' && canAccept && (
                    <Button size="sm" disabled={!choice[c.clinicId]} loading={accept.isPending && accept.variables === c.clinicId} onClick={() => accept.mutate(c.clinicId)}>Accept</Button>
                  )}
                  {c.status === 'active' && <span className="text-[12px] text-[#4d6072]">since {shortDate(c.linkedAt)}</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {accept.error && <p className="mt-3 text-sm text-red-600">{errorText(accept.error)}</p>}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Points of contact (S-17)
 * ------------------------------------------------------------------ */

interface Contact {
  id: string;
  name: string;
  roleTitle: string | null;
  level: 'primary' | 'backup';
  handles: string[];
  channels: 'email' | 'sms' | 'both';
  email: string | null;
  emailVerified: boolean;
  phone: string | null;
  phoneVerified: boolean;
  smsOptedOut: boolean;
  hoursText: string | null;
  reachable: string[];
}
type ContactForm = { name: string; roleTitle: string; level: 'primary' | 'backup'; handles: string[]; channels: 'email' | 'sms' | 'both'; email: string; phone: string; hoursText: string };
const EMPTY: ContactForm = { name: '', roleTitle: '', level: 'primary', handles: ['all'], channels: 'email', email: '', phone: '', hoursText: '' };
const HANDLE_LABEL: Record<string, string> = { rx_issues: 'Rx issues', pricing: 'Pricing', all: 'Everything' };
const CHANNEL_LABEL = { email: 'Email', sms: 'Text', both: 'Email and text' } as const;

function ContactEditor({ initial, id, onClose }: { initial: ContactForm; id: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial);
  const save = useMutation({
    mutationFn: () => (id ? pharmacyApi.put(`/contacts/${id}`, form) : pharmacyApi.post('/contacts', form)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pharmacy-contacts'] });
      void qc.invalidateQueries({ queryKey: ['pharmacy-me'] });
      onClose();
    },
  });
  const set = (patch: Partial<ContactForm>) => setForm({ ...form, ...patch });
  const toggleHandle = (h: string) => {
    if (h === 'all') return set({ handles: ['all'] });
    const rest = form.handles.filter((x) => x !== 'all');
    set({ handles: rest.includes(h) ? rest.filter((x) => x !== h) : [...rest, h] });
  };
  return (
    <div className="rounded-lg border border-hairline bg-white p-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Name"><input className={inputClass} value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
        <Field label="Role"><input className={inputClass} value={form.roleTitle} placeholder="Pharmacist in charge" onChange={(e) => set({ roleTitle: e.target.value })} /></Field>
        <Field label="Primary or backup" hint="Backups are told when a request goes past your escalation time.">
          <select className={inputClass} value={form.level} onChange={(e) => set({ level: e.target.value as ContactForm['level'] })}>
            <option value="primary">Primary</option>
            <option value="backup">Backup</option>
          </select>
        </Field>
        <Field label="How to reach them">
          <select className={inputClass} value={form.channels} onChange={(e) => set({ channels: e.target.value as ContactForm['channels'] })}>
            <option value="email">Email</option>
            <option value="sms">Text</option>
            <option value="both">Email and text</option>
          </select>
        </Field>
        {form.channels !== 'sms' && <Field label="Email"><input className={inputClass} type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} /></Field>}
        {form.channels !== 'email' && <Field label="Mobile number"><input className={inputClass} inputMode="tel" value={form.phone} placeholder="(555) 123-4567" onChange={(e) => set({ phone: e.target.value })} /></Field>}
        <Field label="Hours"><input className={inputClass} value={form.hoursText} placeholder="Mon to Fri, 8 AM to 6 PM" onChange={(e) => set({ hoursText: e.target.value })} /></Field>
      </div>
      <fieldset className="mt-3">
        <legend className="mb-1.5 text-xs font-semibold text-ink">What they handle</legend>
        <div className="flex flex-wrap gap-2">
          {['rx_issues', 'pricing', 'all'].map((h) => (
            <label key={h} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-[14px] ${form.handles.includes(h) ? 'border-forest bg-mint text-navy' : 'border-hairline text-[#4d6072]'}`}>
              <input type="checkbox" className="accent-forest" checked={form.handles.includes(h)} onChange={() => toggleHandle(h)} />
              {HANDLE_LABEL[h]}
            </label>
          ))}
        </div>
      </fieldset>
      {save.error && <p className="mt-3 text-sm text-red-600">{errorText(save.error)}</p>}
      <div className="mt-4 flex gap-2">
        <Button loading={save.isPending} disabled={!form.name.trim() || form.handles.length === 0} onClick={() => save.mutate()}>Save contact</Button>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}

function ContactRow({ c, admin }: { c: Contact; admin: boolean }) {
  const qc = useQueryClient();
  const [note, setNote] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['pharmacy-contacts'] });
    void qc.invalidateQueries({ queryKey: ['pharmacy-me'] });
  };
  const act = useMutation({
    mutationFn: async (what: 'email' | 'code' | 'confirm' | 'test' | 'remove') => {
      if (what === 'email') {
        const r = await pharmacyApi.post<{ note: string; devLink?: string }>(`/contacts/${c.id}/send-email-link`);
        return r.devLink ? `${r.note} While testing, the link is: ${r.devLink}` : r.note;
      }
      if (what === 'code') {
        const r = await pharmacyApi.post<{ note: string; devCode?: string }>(`/contacts/${c.id}/send-phone-code`);
        setCodeSent(true);
        return r.devCode ? `${r.note} While testing, the code is ${r.devCode}.` : r.note;
      }
      if (what === 'confirm') {
        await pharmacyApi.post(`/contacts/${c.id}/confirm-phone`, { code });
        setCodeSent(false);
        refresh();
        return 'Mobile number confirmed.';
      }
      if (what === 'test') {
        const r = await pharmacyApi.post<{ results: Array<{ channel: string; note: string }> }>(`/contacts/${c.id}/test`);
        return r.results.map((x) => `${x.channel === 'sms' ? 'Text' : 'Email'}: ${x.note}`).join(' ');
      }
      await pharmacyApi.delete(`/contacts/${c.id}`);
      refresh();
      return null;
    },
    onSuccess: (text) => setNote(text),
    onError: (error) => setNote(errorText(error)),
  });
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <li className="py-3">
        <ContactEditor id={c.id} onClose={() => setEditing(false)} initial={{ name: c.name, roleTitle: c.roleTitle ?? '', level: c.level, handles: c.handles, channels: c.channels, email: c.email ?? '', phone: c.phone ?? '', hoursText: c.hoursText ?? '' }} />
      </li>
    );
  }
  return (
    <li className="py-3.5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-navy">{c.name}{c.roleTitle ? <span className="font-normal text-[#4d6072]">, {c.roleTitle}</span> : null}</p>
          <p className="mt-0.5 text-[13px] text-[#4d6072]">{c.handles.map((h) => HANDLE_LABEL[h]).join(', ')} by {CHANNEL_LABEL[c.channels].toLowerCase()}{c.hoursText ? `, ${c.hoursText}` : ''}</p>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
            {c.email && <span>{c.email} {c.emailVerified ? <Chip tone="green">Verified</Chip> : <Chip tone="amber">Not verified</Chip>}</span>}
            {c.phone && <span>{c.phone} {c.smsOptedOut ? <Chip>Texted STOP</Chip> : c.phoneVerified ? <Chip tone="green">Verified</Chip> : <Chip tone="amber">Not verified</Chip>}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={c.level === 'primary' ? 'green' : 'grey'}>{c.level === 'primary' ? 'Primary' : 'Backup'}</Chip>
        </div>
      </div>
      {admin && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {c.email && !c.emailVerified && c.channels !== 'sms' && <Button size="sm" variant="secondary" loading={act.isPending && act.variables === 'email'} onClick={() => act.mutate('email')}>Send email link</Button>}
          {c.phone && !c.phoneVerified && c.channels !== 'email' && <Button size="sm" variant="secondary" loading={act.isPending && act.variables === 'code'} onClick={() => act.mutate('code')}>Send code by text</Button>}
          {c.reachable.length > 0 && <Button size="sm" variant="secondary" loading={act.isPending && act.variables === 'test'} onClick={() => act.mutate('test')}>Send a test notification</Button>}
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => act.mutate('remove')}>Remove</Button>
        </div>
      )}
      {codeSent && (
        <div className="mt-2 flex max-w-[360px] items-end gap-2">
          <Field label="6-digit code"><input className={inputClass} inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></Field>
          <Button size="sm" disabled={code.length !== 6} loading={act.isPending && act.variables === 'confirm'} onClick={() => act.mutate('confirm')}>Confirm</Button>
        </div>
      )}
      {note && <p className="mt-2 break-words text-[13px] text-[#4d6072]" role="status">{note}</p>}
    </li>
  );
}

export function PharmacyContactsPage() {
  const me = usePharmacyMe();
  const admin = me.data?.user.role === 'admin';
  const contacts = useQuery({ queryKey: ['pharmacy-contacts'], queryFn: () => pharmacyApi.get<{ contacts: Contact[] }>('/contacts') });
  const [adding, setAdding] = useState(false);
  if (contacts.error) return <ErrorState error={contacts.error} retry={() => void contacts.refetch()} />;
  if (!contacts.data) return <LoadingRows rows={4} />;
  return (
    <Card
      title="Points of contact"
      subtitle="Who MCOS notifies about clinic requests and pricing. Each email and number is verified before it is used. Clinics see names and roles only."
      action={admin && !adding ? <Button size="sm" onClick={() => setAdding(true)}>Add a contact</Button> : undefined}
    >
      {adding && <div className="mb-4"><ContactEditor id={null} initial={EMPTY} onClose={() => setAdding(false)} /></div>}
      {contacts.data.contacts.length === 0 && !adding ? (
        <EmptyState title="No contacts yet" body="Add at least one primary contact for Rx issues and one for pricing." />
      ) : (
        <ul className="divide-y divide-hairline">{contacts.data.contacts.map((c) => <ContactRow key={c.id} c={c} admin={admin} />)}</ul>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Users
 * ------------------------------------------------------------------ */

interface UserRow { id: string; name: string; email: string; role: PharmacyRole; status: string; lastLoginAt: string | null }
const ROLE_LABEL: Record<PharmacyRole, string> = { admin: 'Admin, everything', support: 'Support, the inbox', pricing: 'Pricing, price sheets' };

export function PharmacyUsersPage() {
  const qc = useQueryClient();
  const me = usePharmacyMe();
  const users = useQuery({ queryKey: ['pharmacy-users'], queryFn: () => pharmacyApi.get<{ users: UserRow[] }>('/users') });
  const [form, setForm] = useState({ name: '', email: '', role: 'support' as PharmacyRole });
  const [invite, setInvite] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => pharmacyApi.post<{ inviteUrl: string }>('/users', form),
    onSuccess: (r) => {
      setInvite(r.inviteUrl);
      setForm({ name: '', email: '', role: 'support' });
      void qc.invalidateQueries({ queryKey: ['pharmacy-users'] });
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => pharmacyApi.delete(`/users/${id}`), onSuccess: () => void qc.invalidateQueries({ queryKey: ['pharmacy-users'] }) });
  if (users.error) return <ErrorState error={users.error} retry={() => void users.refetch()} />;
  if (!users.data) return <LoadingRows rows={4} />;
  return (
    <div className="space-y-5">
      <Card title="Users" subtitle="Everyone signs in with a password and an authenticator app.">
        <Table minWidth={600}>
          <thead><tr><Th>Name</Th><Th>Role</Th><Th>Status</Th><Th align="right">{''}</Th></tr></thead>
          <tbody>
            {users.data.users.filter((u) => u.status !== 'removed').map((u) => (
              <tr key={u.id}>
                <Td><div className="font-semibold text-navy">{u.name}</div><div className="text-[12px] text-[#4d6072]">{u.email}</div></Td>
                <Td>{ROLE_LABEL[u.role]}</Td>
                <Td>{u.status === 'active' ? <Chip tone="green">Active</Chip> : u.status === 'setting_up' ? <Chip tone="amber">Setting up</Chip> : <Chip tone="amber">Invited</Chip>}</Td>
                <Td align="right">{u.id !== me.data?.user.id && <Button size="sm" variant="ghost" onClick={() => remove.mutate(u.id)}>Remove</Button>}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      <Card title="Invite someone">
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Name"><input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Email"><input className={inputClass} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Role">
            <select className={inputClass} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as PharmacyRole })}>
              {(Object.keys(ROLE_LABEL) as PharmacyRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </Field>
        </div>
        {add.error && <p className="mt-3 text-sm text-red-600">{errorText(add.error)}</p>}
        <div className="mt-4"><Button loading={add.isPending} disabled={!form.name.trim() || !form.email.includes('@')} onClick={() => add.mutate()}>Create invitation</Button></div>
        {invite && (
          <p className="mt-3 break-all rounded-md bg-mint px-3 py-2 text-[13px] text-navy" role="status">
            Send this link to them. It works once, for 7 days: {invite}
          </p>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Settings (PH-012)
 * ------------------------------------------------------------------ */

export function PharmacySettingsPage() {
  const qc = useQueryClient();
  const me = usePharmacyMe();
  const [form, setForm] = useState({ replyTargetHours: 24, escalateAfterHours: 48, supportHoursText: '', mainPhone: '', website: '' });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (me.data) {
      const p = me.data.pharmacy;
      setForm({ replyTargetHours: p.replyTargetHours, escalateAfterHours: p.escalateAfterHours, supportHoursText: p.supportHoursText ?? '', mainPhone: p.mainPhone ?? '', website: p.website ?? '' });
    }
  }, [me.data]);
  const save = useMutation({
    mutationFn: () => pharmacyApi.put('/settings', form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pharmacy-me'] });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });
  if (!me.data) return <LoadingRows rows={4} />;
  return (
    <Card title="Reply times and details" subtitle="How fast you answer clinic requests. MCOS limits: a reply within 48 hours at most, backups alerted within 72.">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Reply needed within, hours" hint="Default 24. Shown to clinics as the due time on each request.">
          <input className={inputClass} inputMode="numeric" value={form.replyTargetHours} onChange={(e) => setForm({ ...form, replyTargetHours: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="Alert backup contacts after, hours" hint="Default 48. The clinic then sees the request as overdue.">
          <input className={inputClass} inputMode="numeric" value={form.escalateAfterHours} onChange={(e) => setForm({ ...form, escalateAfterHours: Number(e.target.value) || 0 })} />
        </Field>
        <Field label="Support hours"><input className={inputClass} value={form.supportHoursText} onChange={(e) => setForm({ ...form, supportHoursText: e.target.value })} /></Field>
        <Field label="Main phone"><input className={inputClass} value={form.mainPhone} onChange={(e) => setForm({ ...form, mainPhone: e.target.value })} /></Field>
        <Field label="Website"><input className={inputClass} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
      </div>
      {save.error && <p className="mt-3 text-sm text-red-600">{errorText(save.error)}</p>}
      <div className="mt-4 flex items-center gap-3">
        <Button loading={save.isPending} disabled={form.replyTargetHours < 1 || form.replyTargetHours > 48 || form.escalateAfterHours > 72 || form.escalateAfterHours <= form.replyTargetHours} onClick={() => save.mutate()}>Save</Button>
        {saved && <span className="text-sm text-forest" role="status">Saved</span>}
      </div>
    </Card>
  );
}
